import { DigitalTwinSettingCreationRequest } from 'api/digitalTwin/digitalTwin.api'
import type { DigitalTwinSetting, DigitalTwinTimeSeriesSetting } from 'api/digitalTwin/digitalTwin.types'
import i18n from 'i18next'
import Delta from 'quill-delta'
import { useMemo } from 'react'
import { colors } from 'styles/variables'
import Datetime from 'utils/datetime/datetime'
import { z } from 'zod'
export type EventStatus = 'Draft' | 'Sent' | 'Active' | 'Inactive'

export type PermittedActionsEventPost =
  | 'send'
  | 'update'
  | 'unsend'
  | 'activate'
  | 'deactivate'
  | 'reactivate'
  | 'delete_event'
  | 'create_change_request'

export type PermittedActionsChangeRequest = 'update' | 'approve' | 'reject' | 'delete_change_request'

export type EventPost = {
  digital_twin_id: number | undefined
  system_id?: number | undefined
  id?: number
  status: EventStatus
  start_time: ISODateTime
  end_time: ISODateTime
  name?: string
  input_field?: {
    ProductionEvent?: string
    GridEvent?: string
  }
  operational_event_type?: 'ProductionEvent' | 'GridEvent'
  schedulability?: 'MoveableWithinLimits' | 'AlwaysMoveable' | 'NotMoveable'
  schedulability_effect?: string
  earliest_start_time?: ISODateTime
  latest_end_time?: ISODateTime
  category_id?: number
  created_at?: ISODateTime
  updated_at?: ISODateTime
  deleted_at?: ISODateTime
  created_by?: string
  updated_by?: string
  tags?: string[]
  require_umm: boolean
  cancelable_after_start?: boolean
  write?: boolean
  created_by_id?: number
  pending_change_request_id?: number
  change_requests?: EventChangeRequest[]
  setting_instructions?: LinkedObjectsSettingInstructions
  permitted_actions?: Array<
    'send' | 'update' | 'unsend' | 'activate' | 'deactivate' | 'reactivate' | 'delete_event' | 'create_change_request'
  >
}

const SettingInstructionBaseSchema = z.object({
  id: z.number().optional(),
  level: z.string(),
  name: z.string(),
  attribute: z.string(),
  priority: z.number(),
  comment: z.string(),
  start_time: z.string().optional(),
  end_time: z.string().optional(),
  creator_name: z.string().optional(),
  updated_at: z.string().optional(),
})
const SettingInstructionSchema = SettingInstructionBaseSchema.extend({
  value: z.number(),
})

const TimeSeriesSettingInstructionSchema = SettingInstructionBaseSchema.extend({
  interval: z.number().default(3600).optional(),
  values: z.array(z.number()),
})

const LinkedObjectSchema = z.object({
  level: z.string(),
  name: z.string(),
  attribute: z.string().nullable(),
  setting_instructions: z.array(SettingInstructionSchema),
  time_series_setting_instructions: z.array(TimeSeriesSettingInstructionSchema),
  write: z.boolean().optional(),
})

export const LinkedObjectsSettingInstructionsSchema = z.object({
  linked_objects: z.array(LinkedObjectSchema),
})

export type LinkedObjectsSettingInstructions = z.infer<typeof LinkedObjectsSettingInstructionsSchema>
export type LinkedObjectSettingInstructions = z.infer<typeof LinkedObjectSchema>

export type SettingInstruction = z.infer<typeof SettingInstructionSchema>
export type TimeSeriesSettingInstruction = z.infer<typeof TimeSeriesSettingInstructionSchema>

export type EventPostListItem = Omit<EventPost, 'input_field'>

export type EventCategory = {
  id: number | null
  name: string | null
  category_group: {
    id: number | null
    name: string | undefined
  }
}

export type EventPostInputTemplate = {
  event_type: string
  template: string
}

export type EventConfig = {
  categories?: EventCategory[]
  templates?: EventPostInputTemplate[]
  tags?: string[]
  statuses?: string[]
  types?: ('ProductionEvent' | 'GridEvent')[]
  schedulability_effects?: string[]
}

export type EventProperties = {
  attribute: string
  write?: boolean
  settings?: DigitalTwinSetting[]
  time_series_settings?: DigitalTwinTimeSeriesSetting[]
  level?: string
  name?: string
}[]

export type EventChangeRequest = {
  operational_event_id?: number
  comment?: string
  request_status: 'Pending' | 'Approved' | 'Rejected'
  id?: number
  name: string
  input_field?: {
    ProductionEvent?: string
    GridEvent?: string
  }
  operational_event_type?: 'ProductionEvent' | 'GridEvent'
  schedulability?: 'MoveableWithinLimits' | 'AlwaysMoveable' | 'NotMoveable'
  schedulability_effect?: string
  start_time: ISODateTime
  end_time: ISODateTime
  earliest_start_time?: ISODateTime
  latest_end_time?: ISODateTime
  category_id?: number
  require_umm: boolean
  cancelable_after_start: boolean
  status: EventStatus
  system_id?: number
  digital_twin_id?: number
  created_by?: string
  created_by_id?: number
  updated_by?: string
  created_at?: ISODateTime
  updated_at?: ISODateTime
  setting_instructions?: LinkedObjectsSettingInstructions
  permitted_actions: Array<'update' | 'approve' | 'reject' | 'delete_change_request'>
}

export type SettingOrTimeSeriesInstructions = SettingInstruction | TimeSeriesSettingInstruction

type EditableEventPost = EventPost | EventChangeRequest

export const EMPTY_LINKED_OBJECT_ATTRIBUTE = ''

type ComparableEditableEventPost = Pick<
  EditableEventPost,
  | 'name'
  | 'operational_event_type'
  | 'category_id'
  | 'start_time'
  | 'end_time'
  | 'earliest_start_time'
  | 'latest_end_time'
  | 'schedulability'
  | 'schedulability_effect'
  | 'require_umm'
  | 'input_field'
  | 'cancelable_after_start'
  | 'setting_instructions'
>

const EMPTY_RICH_TEXT_VALUES = new Set(['', '<p><br></p>', '<div><br></div>'])

// Entities that Quill/browsers inject during round-trips (single-pass decode, no double-decode risk)
const HTML_ENTITY_DECODE_MAP: Record<string, string> = {
  '&nbsp;': ' ',
  '&quot;': '"',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&#39;': "'",
  '&apos;': "'",
}

const HTML_ENTITY_PATTERN = /&(?:nbsp|quot|amp|lt|gt|#39|apos);/g

/**
 * Canonicalises rich-text HTML for comparison only: decodes HTML entities,
 * collapses whitespace, and treats empty-editor variants as `undefined`.
 */
export function normalizeRichTextComparisonValue(value?: string | null): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }

  const decoded = value.replace(HTML_ENTITY_PATTERN, (entity) => HTML_ENTITY_DECODE_MAP[entity] ?? entity)
  const normalizedValue = decoded.replace(/\s+/g, ' ').trim()

  if (EMPTY_RICH_TEXT_VALUES.has(normalizedValue)) {
    return undefined
  }

  return normalizedValue
}

export function normalizeLinkedObjectAttribute(attribute?: string | null): string {
  return attribute ?? EMPTY_LINKED_OBJECT_ATTRIBUTE
}

function normalizeInputFieldForComparison(
  inputField: EditableEventPost['input_field'],
  eventConfig?: EventConfig
): EditableEventPost['input_field'] | undefined {
  if (!inputField) {
    return undefined
  }

  const normalizedInputField: NonNullable<EditableEventPost['input_field']> = {}

  ;(['ProductionEvent', 'GridEvent'] as const).forEach((eventType) => {
    const normalizedValue = normalizeRichTextComparisonValue(inputField[eventType])
    const normalizedTemplate = normalizeRichTextComparisonValue(getDescriptionFieldTemplate(eventType, eventConfig))

    if (normalizedValue && normalizedValue !== normalizedTemplate) {
      normalizedInputField[eventType] = normalizedValue
    }
  })

  return Object.keys(normalizedInputField).length > 0 ? normalizedInputField : undefined
}

function normalizeLinkedObjectsSettingInstructions(
  settingInstructions: EditableEventPost['setting_instructions']
): EditableEventPost['setting_instructions'] | undefined {
  if (!settingInstructions) {
    return undefined
  }

  return {
    linked_objects: settingInstructions.linked_objects.map((linkedObject) => ({
      ...linkedObject,
      attribute: normalizeLinkedObjectAttribute(linkedObject.attribute),
    })),
  }
}

function normalizeNullishComparisonValue<T>(value: T | null | undefined): T | undefined {
  return value == null ? undefined : value
}

export function getComparableEventPost(
  eventPost: EditableEventPost,
  eventConfig?: EventConfig
): ComparableEditableEventPost {
  const comparableEventPost: ComparableEditableEventPost = {
    name: normalizeNullishComparisonValue(eventPost.name),
    operational_event_type: normalizeNullishComparisonValue(eventPost.operational_event_type),
    category_id: normalizeNullishComparisonValue(eventPost.category_id),
    start_time: eventPost.start_time,
    end_time: eventPost.end_time,
    earliest_start_time: normalizeNullishComparisonValue(eventPost.earliest_start_time),
    latest_end_time: normalizeNullishComparisonValue(eventPost.latest_end_time),
    schedulability: normalizeNullishComparisonValue(eventPost.schedulability),
    schedulability_effect: normalizeNullishComparisonValue(eventPost.schedulability_effect),
    require_umm: eventPost.require_umm,
    input_field: normalizeInputFieldForComparison(eventPost.input_field, eventConfig),
    cancelable_after_start: normalizeNullishComparisonValue(eventPost.cancelable_after_start),
    setting_instructions: normalizeLinkedObjectsSettingInstructions(eventPost.setting_instructions),
  }

  if (eventPost.schedulability === 'NotMoveable') {
    return {
      ...comparableEventPost,
      earliest_start_time: undefined,
      latest_end_time: undefined,
      schedulability_effect: undefined,
    }
  }

  if (eventPost.schedulability === 'AlwaysMoveable') {
    return {
      ...comparableEventPost,
      earliest_start_time: undefined,
      latest_end_time: undefined,
    }
  }

  return comparableEventPost
}

export function getEventPostForSchedulabilityChange<T extends EditableEventPost>(
  eventPost: T,
  schedulability: T['schedulability']
): T {
  const updatedEventPost = {
    ...eventPost,
    schedulability,
  }

  if (schedulability === 'NotMoveable') {
    return {
      ...updatedEventPost,
      earliest_start_time: undefined,
      latest_end_time: undefined,
      schedulability_effect: undefined,
    }
  }

  if (schedulability === 'AlwaysMoveable') {
    return {
      ...updatedEventPost,
      earliest_start_time: undefined,
      latest_end_time: undefined,
    }
  }

  if (schedulability === 'MoveableWithinLimits') {
    return {
      ...updatedEventPost,
      earliest_start_time:
        updatedEventPost.earliest_start_time ||
        Datetime.toISOString(Datetime.subtractHours(updatedEventPost.start_time, 24)),
      latest_end_time:
        updatedEventPost.latest_end_time || Datetime.toISOString(Datetime.addHours(updatedEventPost.end_time, 24)),
    }
  }

  return updatedEventPost
}

export function isEquivalentEditorContent(content: string, currentValue?: string, displayedValue?: string): boolean {
  const normalizedContent = normalizeRichTextComparisonValue(content) ?? ''
  const normalizedCurrentValue = normalizeRichTextComparisonValue(currentValue) ?? ''
  const normalizedDisplayedValue = normalizeRichTextComparisonValue(displayedValue) ?? ''

  return normalizedContent === normalizedCurrentValue || normalizedContent === normalizedDisplayedValue
}

export function hasInitialEventPostChanges(
  eventPost: EventPost,
  initialEventPost: EventPost,
  eventConfig?: EventConfig
): boolean {
  const { id: _eventId, ...eventWithoutId } = eventPost
  const { id: _initialId, ...initialWithoutId } = initialEventPost

  return checkIfEditing(eventWithoutId as EventPost, initialWithoutId as EventPost, eventConfig)
}

export function getLabelForEventStatus(status: EventStatus): string {
  let statusLabel: string
  if (status === 'Draft') {
    statusLabel = i18n.t('Draft')
  } else if (status === 'Sent') {
    statusLabel = i18n.t('Submitted')
  } else if (status === 'Active') {
    statusLabel = i18n.t('Active')
  } else if (status === 'Inactive') {
    statusLabel = i18n.t('Inactive')
  } else {
    statusLabel = status
  }
  return statusLabel
}

export function getColorForEventStatus(status: EventStatus, pendingChangeRequestId: number | null = null): string {
  let color: string
  if (pendingChangeRequestId) {
    color = colors.warning4
  } else if (status === 'Draft') {
    color = '#cda294'
  } else if (status === 'Sent') {
    color = colors.info3
  } else if (status === 'Active') {
    color = colors.success3
  } else if (status === 'Inactive') {
    color = colors.grey5
  } else {
    color = colors.primary3
  }
  return color
}

export const EVENT_TYPES = [
  { value: 'ProductionEvent', label: 'Production event' },
  { value: 'GridEvent', label: 'Grid event' },
]

export const SCHEDULABILITIES = [
  { value: 'MoveableWithinLimits', label: 'Movable within limits' },
  { value: 'AlwaysMoveable', label: 'Always movable' },
  { value: 'NotMoveable', label: 'Not movable' },
]

export function compareEvents(a: EventPostListItem, b: EventPostListItem): number {
  if (a.status === 'Active' && b.status !== 'Active') {
    return 1
  }

  if (a.status !== 'Active' && b.status === 'Active') {
    return -1
  }

  if (a.id === undefined || b.id === undefined) {
    return 0
  }

  return a.id - b.id
}
export function getDescriptionFieldTemplate(operationalEventType: string, eventConfig?: EventConfig): string {
  if (!eventConfig || !eventConfig.templates) {
    return ''
  }
  return (
    eventConfig.templates.find((template: EventPostInputTemplate) => template.event_type === operationalEventType)
      ?.template || ''
  )
}

export const MAX_SCHEDULABILITY_EFFECT_INPUT_LENGTH = 200

export function hasSettingsOutsideTimePeriod(
  settings: DigitalTwinSetting[] | SettingOrTimeSeriesInstructions[],
  eventPost: EventPost | EventChangeRequest
): boolean {
  if (!settings) {
    return false
  }

  return settings?.some((setting) => {
    if (!setting.start_time || !setting.end_time) {
      return false
    }

    const startsBeforeEvent = Datetime.isBefore(
      Datetime.toISOString(setting.start_time),
      Datetime.toISOString(eventPost.start_time)
    )
    const endsAfterEvent = Datetime.isAfter(
      Datetime.toISOString(setting.end_time),
      Datetime.toISOString(eventPost.end_time)
    )
    return startsBeforeEvent || endsAfterEvent
  })
}

export const reactQuillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ align: ['right', 'center', 'justify'] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image'],
  ],
}

export function useInputFieldEditorContent(eventPost: EventPost | EventChangeRequest, eventConfig?: EventConfig) {
  return useMemo(() => {
    const currentEventType = eventPost.operational_event_type
    if (!currentEventType) return ''

    const value = eventPost?.input_field?.[currentEventType]
    if (typeof value === 'string') {
      return value.trim() === '' ? getDescriptionFieldTemplate(currentEventType, eventConfig) : value
    }
    if (!value) {
      return getDescriptionFieldTemplate(currentEventType, eventConfig)
    }
    return value
  }, [eventPost.operational_event_type, eventPost.input_field, eventConfig])
}

export const MAX_IMAGE_SIZE_MB = 2.5

function isBase64ImageSource(value: string): boolean {
  return /^data:[^,]+;base64,/.test(value)
}

export function hasOversizedEmbeddedImage(
  delta: Pick<Delta, 'ops'>,
  maxImageSizeMb: number = MAX_IMAGE_SIZE_MB
): boolean {
  return delta.ops.some((op: any) => {
    const imageValue = op.insert && typeof op.insert === 'object' ? op.insert.image : undefined
    if (typeof imageValue !== 'string' || !isBase64ImageSource(imageValue)) {
      return false
    }

    const base64Data = imageValue.includes(';base64,') ? imageValue.split(';base64,')[1] : imageValue
    const base64Size = base64Data.length * (3 / 4) - (base64Data.endsWith('==') ? 2 : base64Data.endsWith('=') ? 1 : 0)

    return base64Size > maxImageSizeMb * 1024 * 1024
  })
}

export const getNumberOfSettingInstructions = (
  settingInstructions: any[] | undefined,
  eventPost: EventPost | EventChangeRequest | undefined,
  index: number
): number => {
  if (
    !settingInstructions ||
    !eventPost?.setting_instructions?.linked_objects ||
    eventPost.setting_instructions.linked_objects.length === 0
  ) {
    return 0
  }

  const settingsForProperty =
    settingInstructions?.filter(
      (setting) =>
        setting.name === eventPost?.setting_instructions?.linked_objects[index]?.name &&
        setting.attribute === eventPost?.setting_instructions?.linked_objects[index]?.attribute &&
        setting.level === eventPost?.setting_instructions?.linked_objects[index]?.level
    ) ?? []
  return settingsForProperty.length
}

export const noSettingWritePermissionSettingInstructions = (
  eventPost: EventPost | EventChangeRequest | undefined,
  index: number
) => eventPost?.setting_instructions?.linked_objects[index]?.write === false

export const hasDuplicateLinkedObjects = (
  linkedObjects: LinkedObjectsSettingInstructions['linked_objects'] | undefined
): boolean => {
  if (!linkedObjects) return false

  return linkedObjects.some((property, index) => {
    return linkedObjects.some((otherProperty, otherIndex) => {
      if (index === otherIndex) return false

      return (
        property.level === otherProperty.level &&
        property.name === otherProperty.name &&
        normalizeLinkedObjectAttribute(property.attribute) === normalizeLinkedObjectAttribute(otherProperty.attribute)
      )
    })
  })
}

export const noWritePermission = (eventPost: EventPost) => eventPost?.write === false

export const createdAndUpdatedInfoEventPost = (eventPost: EventPost, t: (key: string) => string): string => {
  const createdInfo = eventPost.created_at
    ? `${t('Created by')}: ${eventPost.created_by ? eventPost.created_by : t('Unknown')} ${Datetime.toLocalTime(Datetime.toISOString(eventPost.created_at), 'longDay')}`
    : ``

  const updatedInfo = eventPost.updated_at
    ? `${t('Last updated')}: ${Datetime.toLocalTime(Datetime.toISOString(eventPost.updated_at), 'longDay')} ${t('by')} ${eventPost.updated_by ? eventPost.updated_by : t('Unknown')}`
    : ``

  if (eventPost.updated_at) {
    return `${createdInfo}, ${updatedInfo}`
  } else if (eventPost.created_by) {
    return createdInfo
  } else {
    return ''
  }
}

export const createdAndUpdatedInfoChangeRequest = (
  changeRequest: EventChangeRequest,
  t: (key: string) => string
): string => {
  const createdInfo = changeRequest.created_at
    ? `${t('Change requested by')}: ${changeRequest.created_by ? changeRequest.created_by : t('Unknown')} ${Datetime.toLocalTime(Datetime.toISOString(changeRequest.created_at), 'longDay')}`
    : ``

  const updatedInfo = changeRequest.updated_at
    ? `${t('Updated by')}: ${Datetime.toLocalTime(Datetime.toISOString(changeRequest.updated_at), 'longDay')} ${t('by')} ${changeRequest.updated_by ? changeRequest.updated_by : t('Unknown')}`
    : ``

  if (changeRequest.updated_at) {
    return `${createdInfo}, ${updatedInfo}`
  } else if (changeRequest.created_by) {
    return createdInfo
  } else {
    return ''
  }
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null && b == null) return true
  if (a == null || b == null) return false

  if (typeof a !== typeof b) return false

  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false
    return a.every((item, index) => deepEqual(item, b[index]))
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const aObj = a as Record<string, unknown>
    const bObj = b as Record<string, unknown>
    const aKeys = Object.keys(aObj)
    const bKeys = Object.keys(bObj)

    if (aKeys.length !== bKeys.length) return false
    return aKeys.every((key) => Object.hasOwn(bObj, key) && deepEqual(aObj[key], bObj[key]))
  }

  return a === b
}

export type InputFieldDeltas = {
  saved: Delta | null
  current: Delta | null
}

function hasInputFieldDeltaChanges(deltas: InputFieldDeltas): boolean {
  if (deltas.saved == null && deltas.current == null) return false
  if (deltas.saved == null || deltas.current == null) return true
  return deltas.saved.diff(deltas.current).ops.length > 0
}

export const checkIfEditing = (
  newEventPost: EventPost | EventChangeRequest,
  lastSavedValues: EventPost | EventChangeRequest,
  eventConfig?: EventConfig,
  inputFieldDeltas?: InputFieldDeltas
): boolean => {
  const newComparable = getComparableEventPost(newEventPost, eventConfig)
  const savedComparable = getComparableEventPost(lastSavedValues, eventConfig)

  if (inputFieldDeltas?.saved != null) {
    // Use Delta-based comparison for input_field — immune to HTML serialization quirks
    const nonInputFieldChanged = !deepEqual(
      { ...newComparable, input_field: undefined },
      { ...savedComparable, input_field: undefined }
    )
    return nonInputFieldChanged || hasInputFieldDeltaChanges(inputFieldDeltas)
  }

  // Fallback: HTML-normalized comparison (used when Deltas are not available)
  return !deepEqual(newComparable, savedComparable)
}

export function isTimeSeriesSettingInstruction(setting: SettingOrTimeSeriesInstructions | DigitalTwinSetting): boolean {
  if ('values' in setting && Array.isArray((setting as any).values)) {
    //handle time series settings from setting instructions
    return true
  }

  return false
}

export function checkSettingInstructionOverlapsWithEventSettings(
  newSetting: DigitalTwinSettingCreationRequest | null,
  operationalEvent?: EventPost | EventChangeRequest
): boolean {
  //This function checks if the new setting overlaps with any existing settings in the event.
  // - If it has the same id we are editing an existing setting and should not check for overlap with itself and should return false.
  // - If it has different priority level it should be allowed to overlap and therefore return false.
  // - If the time periods overlap and the priority level is the same it should return true and therefor not allow to create the setting.

  if (!operationalEvent || !newSetting) {
    return false
  }

  return (
    operationalEvent.setting_instructions?.linked_objects.some((obj) => {
      const findLinkedObject =
        obj.name === newSetting.name && obj.level === newSetting.level && obj.attribute === newSetting.attribute

      if (!findLinkedObject) {
        return false
      }

      const instructions = [
        ...(obj.time_series_setting_instructions || []),
        ...(obj.setting_instructions || []),
      ].filter((instruction) => instruction.id !== newSetting.id) //filter out instruction with same id as we are editing that one

      return instructions.some((instruction) => {
        const newSettingStart = newSetting.start_time
        const newSettingEnd = newSetting.end_time
        const newSettingPriority = newSetting.priority
        const eventSettingStart = instruction.start_time
        const eventSettingEnd = instruction.end_time
        const eventSettingPriority = instruction.priority

        if (
          newSettingStart &&
          newSettingEnd &&
          eventSettingStart &&
          eventSettingEnd &&
          newSettingPriority &&
          eventSettingPriority
        ) {
          return (
            newSettingStart <= eventSettingEnd &&
            newSettingEnd >= eventSettingStart &&
            newSettingPriority === eventSettingPriority
          )
        }

        return false
      })
    }) ?? false
  )
}

export const generateUniqueId = (id: number, instructions: SettingOrTimeSeriesInstructions[]): number => {
  if (instructions.find((setting) => setting.id === id)) {
    return generateUniqueId(id + 1, instructions)
  }
  return id
}

export const initializeSettingInstructionsWithIds = (
  settingInstructions: EventChangeRequest['setting_instructions'] | EventPost['setting_instructions']
): EventChangeRequest['setting_instructions'] | EventPost['setting_instructions'] => {
  if (!settingInstructions?.linked_objects) {
    return settingInstructions
  }

  const allIds: number[] = []

  const findUnusedId = (startId: number): number => {
    if (allIds.includes(startId)) {
      return findUnusedId(startId + 1)
    }
    return startId
  }

  const updatedLinkedObjectsWithIds = settingInstructions.linked_objects.map((linkedObject) => {
    const settingInstructionsWithIds = linkedObject.setting_instructions.map((setting, index) => {
      let newId = index
      if (allIds.length === 0) {
        allIds.push(index)
      } else {
        newId = findUnusedId(index)
        allIds.push(newId)
      }
      return {
        ...setting,
        id: setting.id ?? newId,
      }
    })

    const timeSeriesSettingInstructionsWithIds = linkedObject.time_series_setting_instructions.map(
      (timeSeriesSetting, index) => {
        let newId = index
        if (allIds.length === 0) {
          allIds.push(index)
        } else {
          newId = findUnusedId(index)
          allIds.push(newId)
        }
        return {
          ...timeSeriesSetting,
          id: timeSeriesSetting.id ?? newId,
        }
      }
    )

    return {
      ...linkedObject,
      setting_instructions: settingInstructionsWithIds,
      time_series_setting_instructions: timeSeriesSettingInstructionsWithIds,
    }
  })

  return { linked_objects: updatedLinkedObjectsWithIds }
}
